import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MatDialogModule, MAT_DIALOG_DATA }
	from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { FileUploadModule } from '@copo/file-upload-new';
import { firstValueFrom } from 'rxjs';

import { WorkerService } from '../../../../../shared/services/worker.service';
import { TranslationService } from '../../../../../shared/services/translation.service';
import {
	DmtFeasibilityRequest, DmtFeasibilityResponse, EfeasibilityInfo,
	ProductCategoryDisplay
} from '../../../../models/efeasibility';
import { EfeasibilityService } from '../../../../services/efeasibility.service';
import { LoadingSpinnerComponent } from '../../../../../shared/components/utils/loading-spinner.component';

interface MessageHandler {
	data: {
		sheetName: string;
		data: unknown[][];
	}[];
}

@Component({
	selector: 'copo-dialog-file-upload-efeasibility',
	templateUrl: './dialog-file-upload-efeasibility.component.html',
	styleUrl: './dialog-file-upload-efeasibility.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		MatDialogModule,
		TranslateModule,
		MatIconModule,
		MatButtonModule,
		MatProgressSpinnerModule,
		FileUploadModule,
		CommonModule,
		LoadingSpinnerComponent
	],
})
export class DialogFileUploadEfeasibilityComponent implements OnInit {
	productCategoriesList: ProductCategoryDisplay[] = [];
	errorMessage = '';
	reader: FileReader = new FileReader();
	efeasibilityInfo: EfeasibilityInfo[] = [];
	isReading = false;

	constructor(
		private readonly dialogRef: MatDialogRef<DialogFileUploadEfeasibilityComponent>,
		private readonly translationService: TranslationService,
		readonly workerService: WorkerService,
		private readonly efeasibilityService: EfeasibilityService,
		@Inject(MAT_DIALOG_DATA) public data: { productCategoriesList: ProductCategoryDisplay[] }
	) {}

	ngOnInit() {
		this.dialogRef.updateSize('auto', 'auto');
		this.productCategoriesList = this.data.productCategoriesList;
	}

	getErrorMsg(): string[] {
		return [this.errorMessage];
	}

	async addAttachments(events: FileList) {
		try {
			this.errorMessage = '';
			const files = Array.from(events);
			this.efeasibilityInfo = [];
			if (files.length > 1) {
				this.errorMessage = this.translationService.getTR('upload.file.count.error.message');
				this.errorMessage = this.errorMessage.replace('-max-count-', '1');
			} else {
				const [file] = files;
				if (file.type !== 'application/vnd.ms-excel'
					&& file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
					this.errorMessage = this.translationService.getTR('fastlane.template.xls.mime.type');
				} else {
					this.isReading = true;
					const arrayBuffer = await file.arrayBuffer();
					this.processExcelContent(arrayBuffer);
				}
			}
		} catch (err) {
			this.errorMessage = this.translationService.getTR('global.unexpected.error.identified');
			throw err;
		}
	}

	private processExcelContent(input: ArrayBuffer | undefined) {
		if (input) {
			const worker = this.workerService.readExcelWorker();

			worker.addEventListener('message', async ({ data }: MessageHandler) => {
				try {
					worker.terminate();
					if (this.validateExcelData(data)) {
						this.efeasibilityInfo = await this.extractEfeasibilityInfo(data);
						this.isReading = false;
						this.dialogRef.close(this.efeasibilityInfo);
					} else {
						this.isReading = false;
					}
				} catch (err) {
					this.isReading = false;
					this.errorMessage = this.translationService.getTR('global.unexpected.error.identified');
					throw err;
				}
			});
			const inputArray = new Uint8Array(input);
			worker.postMessage({ inputArray });
		}
	}

	private async extractEfeasibilityInfo(
		data: { sheetName: string; data: unknown[][] }[]
	): Promise<EfeasibilityInfo[]> {
		const feasibilitySheet = data.find(sheet => sheet.sheetName
			=== this.translationService.getTR('efeasibility.excel.file.name'));
		const efeasibilityInfo: EfeasibilityInfo[] = [];

		if (feasibilitySheet) {
			for (let i = 1; i < feasibilitySheet.data.length; i++) {
				const row = feasibilitySheet.data[i];
				if (row.length !== 0) {
					const filteredCategory = this.productCategoriesList.find(
						category => category.productCategory === <string>row[1]
					);
					const filteredSubCategory = filteredCategory?.productSubCategories.find(
						subCategory => subCategory.productSubCategory === <string>row[2]
					);
					const productCategoryCode = filteredCategory?.productCategoryCode ?? '';
					const productSubCategoryCode = filteredSubCategory?.productSubCategoryCode ?? '';

					const efeasibilityRequest: DmtFeasibilityRequest = {
						productCategoryCode,
						productSubCategoryCode,
						steelGradeStandardName: <string>row[3],
						steelGradeName: <string>row[4],
						widthValue: <string>row[5],
						thicknessValue: <string>row[6]
					};
					let response: DmtFeasibilityResponse | undefined;
					try {
						response = await firstValueFrom(
							this.efeasibilityService.getDmtFeasibility(efeasibilityRequest)
						);
					} catch (error) {
						console.error(error);
						response = undefined;
					}
					const ANSWER_TECHNICAL = this.translationService.getTR('product.feasibility.technical-error');
					const ANSWER_NOT_FEASIBLE = this.translationService.getTR('product.feasibility.not-feasible');
					let answer: string;

					if (!response) {
						answer = ANSWER_TECHNICAL;
					} else if (response.answer) {
						answer = response.answer;
					} else {
						answer = ANSWER_NOT_FEASIBLE;
					}

					const info: EfeasibilityInfo = {
						soldToName: <string>row[0],
						productCategory: <string>row[1],
						productSubCategory: <string>row[2],
						standardSteelGrade: <string>row[3],
						steelGrade: <string>row[4],
						width: Number(row[5]),
						thickness: Number(row[6]),
						answer
					};
					efeasibilityInfo.push(info);
				}
			}
		}
		return efeasibilityInfo;
	}

	private validateExcelData(data: { sheetName: string; data: unknown[][] }[]): boolean {
		const expectedSheetNames = [
			this.translationService.getTR('guarantee.customer.name'),
			this.translationService.getTR('datacenter-filter.product-category'),
			this.translationService.getTR('efeasibility.excel.file.name'),
			this.translationService.getTR('dashboard.steelgrade')
		];

		// Extract sheet names from the data
		const sheets = new Set(data.map(sheet => sheet.sheetName));

		// Check if the Excel file contains exactly 4 sheets with the specified names
		const hasAllSheets = expectedSheetNames.every(sheet => sheets.has(sheet));

		if (!hasAllSheets) {
			this.errorMessage = this.translationService.getTR('efeasibility.template.error');
			return false;
		}
		const feasibilitySheet = data.find(sheet => sheet.sheetName
			=== this.translationService.getTR('efeasibility.excel.file.name'));
		const soldToNameSheet = data.find(sheet => sheet.sheetName
			=== this.translationService.getTR('guarantee.customer.name'));
		const productCategorySheet = data.find(sheet => sheet.sheetName
			=== this.translationService.getTR('datacenter-filter.product-category'));
		const steelgradeSheet = data.find(sheet => sheet.sheetName
			=== this.translationService.getTR('dashboard.steelgrade'));
		const expectedHeaders = [
			this.translationService.getTR('guarantee.customer.name').trim(),
			this.translationService.getTR('datacenter-filter.product-category').trim(),
			this.translationService.getTR('datacenter.offer.subcategory').trim(),
			this.translationService.getTR('standard-steelgrade').trim(),
			this.translationService.getTR('dashboard.steelgrade').trim(),
			this.translationService.getTR('shipment.filter.width').trim(),
			this.translationService.getTR('shipment.filter.thickness').trim()
		];
		if (feasibilitySheet && soldToNameSheet && productCategorySheet && steelgradeSheet) {
			// Check if the sheet has exactly 5 columns with the expected headers
			if (feasibilitySheet.data[0].length !== expectedHeaders.length
				|| !expectedHeaders.every((header, index) => feasibilitySheet.data[0][index] === header)) {
				this.errorMessage = this.translationService.getTR('efeasibility.template.error');
				return false;
			}

			// Check if there is at least one row with data for these 5 columns
			const hasData = feasibilitySheet.data.slice(1).some(row => row.length === expectedHeaders.length
				&& row.every(cell => cell !== null && cell !== undefined && cell !== ''));

			if (!hasData) {
				this.errorMessage = this.translationService.getTR('no.data.error');
				return false;
			}
			const validSoldToNames = new Set(soldToNameSheet.data.slice(1).map(row => row[0]));
			const validProductCategories = new Set(productCategorySheet.data.slice(1).map(row => row[0]));
			const validProductSubCategories = new Set(productCategorySheet.data.slice(1).map(row => row[1]));
			const validStandardSteelgrades = new Set(steelgradeSheet.data.slice(1).map(row => row[0]));
			const validSteelgrades = new Set(steelgradeSheet.data.slice(1).map(row => row[1]));
			// Validate each row
			for (let i = 1; i < feasibilitySheet.data.length; i++) {
				const row = feasibilitySheet.data[i];
				if (row.length !== 0) {
					if (row[0] === '' || !validSoldToNames.has(row[0])) {
						this.errorMessage = this.translationService
							.getTR('efeasibility.template.soldtoname.validation');
						return false;
					}
					if (row[1] === '' || !validProductCategories.has(row[1])) {
						this.errorMessage = this.translationService
							.getTR('efeasibility.template.product.category.validation');
						return false;
					}
					if (row[2] === '' || !validProductSubCategories.has(row[2])) {
						this.errorMessage = this.translationService
							.getTR('efeasibility.template.product.sub.category.validation');
						return false;
					}
					if (row[3] === '' || !validStandardSteelgrades.has(row[3])) {
						this.errorMessage = this.translationService
							.getTR('efeasibility.template.standard.steelgrade.validation');
						return false;
					}
					if (row[4] === '' || !validSteelgrades.has(row[4])) {
						this.errorMessage = this.translationService
							.getTR('efeasibility.template.steelgrade.validation');
						return false;
					}
					if (Number.isNaN(Number(row[5])) || Number.isNaN(Number(row[6]))) {
						this.errorMessage = this.translationService.getTR('fields.number.error');
						return false;
					}
				}
			}
		} else {
			this.errorMessage = this.translationService
				.getTR('efeasibility.template.error');
			return false;
		}
		return true;
	}
}
